import { useEffect } from 'react';

// Minimal shape of the Screen Wake Lock API — not in every TS lib.dom yet.
interface WakeLockSentinelLike {
  release: () => Promise<void>;
}
interface WakeLockLike {
  request: (type: 'screen') => Promise<WakeLockSentinelLike>;
}

/**
 * Hold a screen wake lock while `active`. The lock is dropped automatically when
 * the tab is hidden, so we re-request it on becoming visible again. Silently
 * no-ops where the API is unsupported (older Safari, etc.).
 */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const wakeLock = (navigator as unknown as { wakeLock?: WakeLockLike }).wakeLock;
    if (!wakeLock) return;

    let sentinel: WakeLockSentinelLike | null = null;
    let cancelled = false;

    const request = async () => {
      try {
        const s = await wakeLock.request('screen');
        if (cancelled) {
          s.release().catch(() => {});
        } else {
          sentinel = s;
        }
      } catch {
        /* user may have denied, or the tab isn't visible — ignore */
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') request();
    };

    request();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      sentinel?.release().catch(() => {});
    };
  }, [active]);
}
